import {
  GstTreatment,
  InvoiceDataEntity,
} from "../../domain/entities/InvoiceEntity";
import type { Currency } from "../../../../services/api";
import { zournalCustomerId } from "../../../../shared/utils/buildManualDocumentJson";
import {
  resolveCurrencyFieldsFromCatalog,
} from "../../../../shared/utils/manualDocumentCurrency";
import {
  getCountryAbbreviation,
  getStateAbbreviation,
} from "../../../../utils";

/** `location.state.returnTo` when opening Add/Edit customer from create-manual invoice flow */
export const CREATE_MANUAL_INVOICE_RETURN_TO = "create-manual-invoice";

export const CREATE_MANUAL_INVOICE_ROUTE = "/invoices/manual/new";

export type CreateManualInvoiceNavigationState = {
  returnTo?: string;
  createManualReturnPath?: string;
  invoiceDraft?: InvoiceDataEntity;
  applyCustomerPatch?: Partial<
    Pick<
      InvoiceDataEntity,
      | "customer_id"
      | "customer_name"
      | "customer_gstin"
      | "customer_address"
      | "place_of_supply"
      | "destination_of_supply"
    >
  >;
};

export type CustomerLikeForManualInvoice = {
  zoho_customerid?: string;
  gst_treatment?: GstTreatment;
  customerid?: string;
  customer_id?: string | number;
  customer_name?: string;
  name?: string;
  gstin_vat_tax_id?: string;
  gstin?: string;
  state?: string | null;
  country?: string | null;
  billing_address?: string;
  address?: string;
  /** From API search — code/symbol resolved via currencies catalog */
  currency_id?: string | number;
};

/**
 * Customer-only fields for manual invoice JSON (vendor place of supply + customer destination).
 */
export function buildManualInvoiceCustomerPatch(
  customer: CustomerLikeForManualInvoice,
  vendorPlaceOfSupplyAbbrev: string,
  currencies: Currency[] = [],
): Pick<
  InvoiceDataEntity,
  | "customer_id"
  | "customer_name"
  | "customer_gstin"
  | "customer_address"
  | "place_of_supply"
  | "destination_of_supply"
  | "source_of_supply"
  | "gst_treatment"
  | "gst_no"
  | "currency_id"
  | "currency_code"
  | "currency_symbol"
> {
  const customerId = zournalCustomerId(customer);
  const customerName = (customer.customer_name || customer.name || "").trim();
  const gstin = (customer.gstin_vat_tax_id || customer.gstin || "").trim();
  const address = (customer.billing_address || customer.address || "").trim();
  const customerState = customer.state ?? "";
  const destState =
    getStateAbbreviation(customerState) ||
    getCountryAbbreviation(customer.country) ||
    "";

  const currencyId = String(customer.currency_id ?? "").trim();
  const currencyResolved = currencyId
    ? resolveCurrencyFieldsFromCatalog(currencies, currencyId)
    : null;

  return {
    customer_id: customerId,
    customer_name: customerName,
    customer_gstin: gstin,
    gst_no: gstin,
    gst_treatment: customer.gst_treatment || "",
    customer_address: address,
    place_of_supply: vendorPlaceOfSupplyAbbrev,
    source_of_supply: vendorPlaceOfSupplyAbbrev,
    destination_of_supply: destState,
    currency_id: currencyResolved?.currency_id ?? currencyId,
    currency_code: currencyResolved?.currency_code ?? "",
    currency_symbol: currencyResolved?.currency_symbol ?? "",
  };
}

export function isCreateManualInvoiceReturnState(
  state: unknown,
): state is CreateManualInvoiceNavigationState & {
  createManualReturnPath: string;
  invoiceDraft: InvoiceDataEntity;
} {
  if (!state || typeof state !== "object") return false;
  const s = state as CreateManualInvoiceNavigationState;
  return (
    s.returnTo === CREATE_MANUAL_INVOICE_RETURN_TO &&
    typeof s.createManualReturnPath === "string" &&
    s.createManualReturnPath.length > 0 &&
    s.invoiceDraft != null &&
    typeof s.invoiceDraft === "object"
  );
}
